// composables/useSwatchesCart.js
import { ref, reactive, inject } from 'vue'
import { useProductHelpers } from './productHelpers'
import { normalizeVariantName, extractShortRoastName } from './roastNameMapper'

export function useSwatchesCart(runEngine, state, page = null) {
  const helpers = useProductHelpers()
  const trackEvent = inject("trackEvent")

  // Modal state for cart operations
  const modal = reactive({
    show: true,
    page: true,
    product: {
      qty: 0,
      packName: "",
      Coupon: [],
      variantID: 40029155524668,
    },
    upsell: {
      show: true,
      activate: false,
    },
    crosssell: {
      show: false,
      variantId: 32453656150076,
      activate: false,
    },
  })

  // Cart methods
  const setProductModal = (input) => {
    modal.product = {
      qty: input.qty,
      packName: input.packName,
      Coupon: input.Coupon,
      variantID: input.variantID,
    }
    modal.crosssell.variantId = input.crossSellID
  }

  const setChoice = (choice) => {
    if (modal.upsell.show === true) {
      modal.upsell.activate = choice
      modal.upsell.show = false
      modal.crosssell.show = true
    } else if (modal.crosssell.show === true) {
      modal.crosssell.activate = choice
      checkOut()
    }
  }

  const checkOut = () => {
    const crossSellId = modal.crosssell.activate && modal.crosssell.variantId
    performCheckout(
      modal.product.variantID,
      modal.upsell.activate ? modal.product.qty * 2 : modal.product.qty,
      modal.upsell.activate ? modal.product.Coupon[0] : modal.product.Coupon[1],
      modal.product.packName,
      null,
      crossSellId,
      trackEvent
    )
  }

  const addUpgradeToCart = (accepted) => {
    accepted ? onCheckout(state.upgrade) : onCheckout(state.regular)
  }

  const onCheckout = (Item) => {
    performCheckout(
      Item.varID,
      Item.quantity,
      Item.Coupon,
      Item.pack,
      null,
      null,
      trackEvent
    )
  }

  const addToCart = () => {
    const item = state.items[state.active.items]
    const variant = item.Variants[state.active.variants]
    const pack = variant.Pack[state.active.packs] 
    let variantID = variant.variant.VariantID
    let quantity = helpers.formatPackSize(pack.PackSize)
    let actualPack = pack
    let sellingPlanIdOverride = null

    const pageData = page?.value || page

    // Check if variant_method is enabled in page.ExtraData
    if (pageData?.ExtraData?.variant_method === true) {
      const result = findVariantByMethod(item, variant, pack, state.active.sellingPlan)
      if (result) {
        variantID = result.variantID
        quantity = 1 // Always add 1 unit when using variant_method
        actualPack = result.pack || pack
        sellingPlanIdOverride = result.sellingPlanId || null
      } else {
        console.warn('⚠ variant_method enabled but no matching variant found')
      }
    }

    if (typeof state.upgrades.CrossSellOffer !== "undefined") {
      handleCrossSellOffer(item, variant, actualPack, variantID)
    } else if (typeof state.upgrades.UpsellPackOffer !== "undefined") {
      handleUpsellPackOffer(item, variant, actualPack, variantID)
    } else if (state.isSubscription && helpers.hasSellingPlans(state.items, state.active.items, state.active.variants)) {
      handleSubscriptionCheckoutWithVariantMethod(variantID, actualPack, quantity, sellingPlanIdOverride)
    } else {
      handleRegularCheckoutWithVariantMethod(variantID, actualPack, quantity)
    }
  }

  /**
   * Finds a variant based on name pattern matching when variant_method is enabled
   * 
   * This method is used for products with custom pricing where variants are named
   * in a specific pattern: "VariantType / RoastType / Quantity"
   * 
   * Example:
   * - User selects: Whole Beans (variant), Grata Medium Roast (item), 3 Bags (pack)
   * - Pattern generated: "Whole Beans / Medium / 3" (short roast name extracted)
   * - Searches for a variant with this exact name
   * - Returns that variant's ID to add 1 unit to cart (instead of 3 units of the base variant)
   * 
   * @param {Object} item - The selected product item (contains roast type)
   * @param {Object} variant - The selected variant (contains bag type: Whole Beans, Ground, Pods)
   * @param {Object} pack - The selected pack (contains quantity like "x3", "x6")
   * @returns {Object|null} - Object with {variantID, pack, searchPattern} or null if not found
   */
  const findVariantByMethod = (item, variant, pack, sellingPlanIndex = null) => {
    try {
      // Extract the pack quantity (e.g., "x3" -> "3")
      const packQuantity = pack.PackSize.replace(/[^0-9]/g, '')
      
      // Get variant name (e.g., "Whole Beans", "Ground", "Pods")
      const variantName = normalizeVariantName(variant.Name)
      
      // Get roast name and extract short version
      const fullRoastName = item.Name.trim()
      const shortRoastName = extractShortRoastName(fullRoastName)
      
      // Construct the search pattern: "VariantName / RoastName / Quantity"
      const searchPattern = `${variantName} / ${shortRoastName} / ${packQuantity}`
      
      // Get ALL variants from Engine (fetched from Shopify GraphQL)
      const allShopifyVariants = runEngine.getVariants()
      
      if (allShopifyVariants.length === 0) {
        console.warn('⚠ No Shopify variants loaded! Make sure fetchProduct was called.')
        console.warn('  Attempting to search in state.items as fallback...')
        
        // Fallback: search in state.items
        for (const productKey in state.items) {
          const product = state.items[productKey]
          if (product && product.Variants) {
            for (const v of product.Variants) {
              const vName = v.Name?.trim()
              if (vName === searchPattern) {
                return {
                  variantID: v.variant.VariantID,
                  pack: v.Pack?.[0] || pack,
                  searchPattern: searchPattern
                }
              }
            }
          }
        }
      }
      
      // Search through ALL Shopify variants
      for (const shopifyVariant of allShopifyVariants) {
        const variantTitle = shopifyVariant.title?.trim()
        
        // Skip if title is undefined or empty
        if (!variantTitle) {
          continue
        }
        
        // Check if variant title matches the pattern
        if (variantTitle === searchPattern || 
            variantTitle.includes(searchPattern) ||
            variantTitle === `${variantName} / ${shortRoastName} / ${packQuantity}`) {
          // Extract variant ID from GID format: "gid://shopify/ProductVariant/123456"
          const variantID = shopifyVariant.id.split('/').pop()

          let sellingPlanId = null
          const sellingPlanAllocations = shopifyVariant.sellingPlanAllocations?.nodes || []
          if (sellingPlanAllocations.length > 0) {
            if (
              sellingPlanIndex !== null &&
              sellingPlanIndex >= 0 &&
              sellingPlanIndex < sellingPlanAllocations.length
            ) {
              sellingPlanId = sellingPlanAllocations[sellingPlanIndex]?.sellingPlan?.id?.split('/').pop() || null
            } else {
              sellingPlanId = sellingPlanAllocations[0]?.sellingPlan?.id?.split('/').pop() || null
            }
          }
          
          return {
            variantID: variantID,
            pack: pack, // Keep the original pack for pricing
            searchPattern: searchPattern,
            sellingPlanId: sellingPlanId
          }
        }
      }
      
      console.warn('✗✗✗ NO MATCH FOUND ✗✗✗')
      console.warn('Pattern was:', searchPattern)
      console.warn('Available variants:', allShopifyVariants.map(v => v.title).filter(t => t !== undefined))
      return null
    } catch (error) {
      console.error('Error in findVariantByMethod:', error)
      console.error('Error details:', error.message)
      return null
    }
  }

  // Modified subscription checkout to accept custom quantity
  const handleSubscriptionCheckoutWithVariantMethod = (variantID, pack, quantity, sellingPlanIdOverride = null) => {
    const sellingPlanId = sellingPlanIdOverride ??
      helpers.getSellingPlan(state.items, state.active.items, state.active.variants, state.active.sellingPlan)

    performCheckout(
      variantID,
      quantity,
      pack.Coupon,
      pack.Name,
      sellingPlanId,
      null,
      trackEvent
    )
  }

  // Modified regular checkout to accept custom quantity
  const handleRegularCheckoutWithVariantMethod = (variantID, pack, quantity) => {
    performCheckout(
      variantID,
      quantity,
      pack.Coupon,
      pack.Name,
      null,
      null,
      trackEvent
    )
  }
  const performCheckout = (variantId, quantity, coupon, packName, sellingPlanId = null, crossSellId = null, trackEvent) => {
    const checkoutItems = [];
    const packReward = state?.packRewards?.[state?.active?.packs];

    if (packReward) {
  
      packReward?.products.forEach((product) => {
        checkoutItems.push({
          id: product.id,
          quantity: product.quantity,
          coupon: product.coupon,
        });
      });
      checkoutItems.push({
        id: variantId,
        quantity: quantity,
        coupon: coupon,
        plan: sellingPlanId,
      });
      if(crossSellId){
        checkoutItems.push({
          id: crossSellId,
          quantity: 1,
          coupon: null,
        });
      }

      runEngine.checkoutWithItems(checkoutItems, trackEvent);
    }else{
      runEngine.checkout(
      variantId,
      quantity,
      coupon,
      packName,
      sellingPlanId,
      crossSellId,
      trackEvent
    );
    }
  };
  // Helper methods for addToCart
  const handleCrossSellOffer = (item, variant, pack, variantID) => {
    document.body.style.overflow = "hidden"
    const upgradeOffer = helpers.findUpgradeOffer(state.items, state.active.items, state.active.variants, pack.PackSize)
    const oferTa = upgradeOffer[0]
    const CrossSellVariant = JSON.parse(
      state.upgrades.CrossSellOffer.VariantId.VariantSKU
    )[helpers.getVariantType(variant.Name)]

    setProductModal({
      qty: helpers.formatPackSize(pack.PackSize),
      packName: pack.Name,
      Coupon: [oferTa.Coupon, pack.Coupon],
      variantID: variantID,
      crossSellID: CrossSellVariant,
    })

    setupUpgradeState(item, variant, pack, variantID, oferTa)
  }

  const handleUpsellPackOffer = (item, variant, pack, variantID) => {
    document.body.style.overflow = "hidden"
    const xUpgradeQty = helpers.findMatchingUpgradeOffer(state.upgrades.UpsellPackOffer, pack.PackSize)
    const oferTi = xUpgradeQty[0]
    const upgradeOffer = helpers.findUpgradeOffer(state.items, state.active.items, state.active.variants, oferTi.PackUpgrade)
    const oferTa = upgradeOffer[0]

    if (state.layoutPopup == "L02") {
      setProductModal({
        qty: helpers.formatPackSize(pack.PackSize),
        packName: pack.Name,
        Coupon: [oferTa.Coupon, pack.Coupon],
        variantID: variantID,
      })
    }

    setupUpsellState(item, variant, pack, variantID, oferTa, oferTi)
  }

  const handleSubscriptionCheckout = (variantID, pack) => {
    performCheckout(
      variantID,
      helpers.formatPackSize(pack.PackSize),
      pack.Coupon,
      pack.Name,
      helpers.getSellingPlan(state.items, state.active.items, state.active.variants, state.active.sellingPlan),
      null,
      trackEvent
    )
  }

  const handleRegularCheckout = (variantID, pack) => {
    performCheckout(
      variantID,
      helpers.formatPackSize(pack.PackSize),
      pack.Coupon,
      pack.Name,
      null,
      null,
      trackEvent
    )
  }

  const setupUpgradeState = (item, variant, pack, variantID, oferTa) => {
    state.upgrade = {
      product: item.Name,
      pack: pack.Name,
      variant: variant.Name,
      varID: variantID,
      quantity: helpers.formatPackSize(pack.PackSize) * 2,
      PackDiff: helpers.formatPackSize(pack.PackSize) / 2,
      UpSell: {
        PreHeader: state.upgrades.UpsellOffer.PreHeader,
        Header: state.upgrades.UpsellOffer.Header,
        SubHeader: state.upgrades.UpsellOffer.SubHeader,
        Image: state.items[state.active.items].Variants[state.active.variants].Pack[state.active.packs].Image.url,
        ButtonText: "Yes! Upgrade My Order",
      },
      CrossSell: {
        Body: state.upgrades.CrossSellOffer.Body,
        Header: state.upgrades.CrossSellOffer.Header,
        Image: state.upgrades.CrossSellOffer.Image.url,
        PricenSave: state.upgrades.CrossSellOffer.PricenSave,
        ButtonText: "Yes! Upgrade My Order",
      },
      OfferType: oferTa.OfferType,
      Discount: oferTa.Discount,
      Coupon: oferTa.Coupon,
      Price: oferTa.Price,
      show: true,
    }
  }

  const setupUpsellState = (item, variant, pack, variantID, oferTa, oferTi) => {
    state.upgrade = {
      product: item.Name,
      pack: pack.Name,
      variant: variant.Name,
      varID: variantID,
      quantity: helpers.formatPackSize(oferTi.PackUpgrade),
      PackDiff: helpers.calculatePackDifference(oferTi.PackUpgrade, oferTi.PackSizeRun),
      PreHeader: oferTi.PreHeader,
      Header: oferTi.Header,
      SubHeader: oferTi.SubHeader,
      Image: oferTi.Image.url,
      OfferType: oferTa.OfferType,
      Discount: oferTa.Discount,
      Coupon: oferTa.Coupon,
      Price: oferTa.Price,
      ButtonText: oferTi.ButtonText,
      show: true,
    }

    state.regular = {
      product: item.Name,
      variant: variant.Name,
      pack: pack.Name,
      quantity: helpers.formatPackSize(pack.PackSize),
      Coupon: pack.Coupon,
      varID: variantID,
    }
  }

  const handleButtonClick = (checkboxChecked) => {
    if (helpers.validateSubscriptionCheckbox(checkboxChecked, state.isSubscription)) {
      return true // Indicate checkbox needs to be checked
    } else {
      addToCart()
      return false
    }
  }

  return {
    modal,
    setProductModal,
    setChoice,
    addUpgradeToCart,
    addToCart,
    handleButtonClick,
  }
}